"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Zap, Maximize2, Truck, ArrowRight, ShieldAlert } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const checklistItems = [{
  id: "power",
  code: "SEC-01",
  classification: "POWER",
  title: "Power Requirements",
  description: "Confirm the electrical requirements for the exact bull, controller and blower. Do not apply one generic voltage or circuit requirement to every machine.",
  icon: Zap
}, {
  id: "space",
  code: "SEC-02",
  classification: "SPACE",
  title: "Operating Footprint",
  description: "Measure the full operating area, including the inflatable safety arena and appropriate access around the equipment.",
  icon: Maximize2
}, {
  id: "access",
  code: "SEC-03",
  classification: "ACCESS",
  title: "Delivery Logistics",
  description: "Consider doors, gates, loading areas, packed dimensions and unloading requirements before delivery.",
  icon: Truck
}];
export default function Sitepreparationchecklist() {
  const shouldReduceMotion = useReducedMotion();
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="sitepreparationchecklist" data-nav="light" className="relative bg-background text-foreground py-20 md:py-28 border-b border-border selection:bg-primary selection:text-primary-foreground">
      {/* Subtle industrial grid line accents */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(hsl(30_13%_82%_/0.25)_1px,transparent_1px)] [background-size:24px_24px] opacity-60" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.3
      }} variants={headerVariants} className="mx-auto max-w-3xl text-center mb-14 md:mb-18">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 mb-4 border border-border bg-card text-muted-foreground font-mono text-xs uppercase tracking-wider">
            <ShieldAlert className="size-3.5 text-primary" />
            <span>Pre-Purchase Advisory</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground">
            Check Power, Space and Access Before You Buy
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed max-w-2xl mx-auto">
            A machine can look perfect online and still be wrong for the location
            if the site cannot support its power, footprint or delivery
            requirements.
          </p>
        </motion.div>

        {/* 3-Column Industrial Spec Plate Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {checklistItems.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.article key={item.id} data-index={i} variants={cardVariants} className="group relative flex flex-col justify-between bg-card p-6 lg:p-8 border border-border rounded-none shadow-xs transition-colors duration-180 hover:border-foreground">
                {/* Top classification row & datum marker */}
                <div>
                  <div className="flex items-center justify-between pb-5 border-b border-border/80">
                    <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
                      {item.code}
                    </span>
                    <span className="font-mono text-xs font-semibold px-2 py-0.5 bg-muted text-foreground uppercase tracking-wider border border-border">
                      {item.classification}
                    </span>
                  </div>

                  {/* Icon & Title */}
                  <div className="mt-6 mb-4 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center border border-border bg-background text-primary group-hover:border-foreground group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-180">
                      <IconComponent className="size-5" />
                    </div>
                    <h3 className="font-serif text-xl sm:text-2xl font-bold uppercase tracking-tight text-foreground">
                      {item.title}
                    </h3>
                  </div>

                  {/* Body description */}
                  <p className="text-sm sm:text-base text-muted-foreground font-default leading-relaxed">
                    {item.description}
                  </p>
                </div>

                {/* Bottom spec datum strip */}
                <div className="mt-8 pt-4 border-t border-border/60 flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <span>SPECIFICATION CHECK</span>
                  <span className="text-foreground font-medium uppercase group-hover:text-primary transition-colors duration-180">
                    MANDATORY
                  </span>
                </div>
              </motion.article>;
        })}
        </motion.div>

        {/* Action Block */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.5
      }} variants={headerVariants} className="mt-12 md:mt-16 text-center">
          <Button asChild size="lg" className="rounded-none font-serif text-base uppercase tracking-wider px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm border border-primary">
            <Link to="/SetupSafetyAndMaintenancePage" className="inline-flex items-center gap-2">
              <span>View Setup & Safety Information</span>
              <ArrowRight className="size-4" />
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}